import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Intent, IntentConfig } from "@/model";
import { useUpdateIntent } from "@/query/intent";
import { useAgentId, useModal } from "@/hook";
import Button from "@/component/Button";
import { Modal } from "@/component/modal";
import { errorToast, successToast } from "@/component/Toast";
import { TextInputGroup, RangeSlider, Toggle } from "@/component/input";

import AnswerEditor from "../AnswerEditor";


export const INTENT_SETTING_MODAL_ID = "intent-setting-modal-id";

const IntentSettingModal: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const agentId = useAgentId();
    const intent: Intent = modal.getData(INTENT_SETTING_MODAL_ID);

    const [validation, setValidation] = useState<{ name: boolean }>({ name: true });
    const [active, setActive] = useState<boolean>();
    const [name, setName] = useState<string>();
    const [answers, setAnswers] = useState<string[]>();
    const [intentThreshold, setIntentThreshold] = useState<IntentConfig>();

    const { mutateAsync: updateIntent, isPending: isUpdating } = useUpdateIntent(
        agentId,
        intent?.skill_id,
        intent?.id
    );

    const validate = () => {
        const currentValidation = {
            name: !!name && name.trim().length > 0
        };
        setValidation(currentValidation);
        return Object.values(currentValidation).every(value => value);
    };

    const onClickConfirm = async () => {
        if (!validate()) {
            return;
        }
        const form = {
            name: name!.trim(),
            active,
            config: intentThreshold,
            answers: answers
        };
        await updateIntent(form)
            .then(() => {
                successToast(intl.formatMessage({ id: "t003" }));
                modal.close(INTENT_SETTING_MODAL_ID);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            });
    };

    useEffect(() => {
        if (!intent) {
            return;
        }
        setName(intent.name);
        setIntentThreshold({
            use_intent_threshold: !!intent.config?.use_intent_threshold,
            intent_threshold: intent.config?.intent_threshold || 50
        });
        setAnswers([...(intent.answers ?? [])]);
        setActive(intent.active === undefined ? true : intent.active);
    }, [intent]);

    return (
        <Modal id={INTENT_SETTING_MODAL_ID}
               size="md"
               title={intl.formatMessage({ id: "i03023" })}
               onClose={() => setValidation({ name: true })}
        >
            <div className="modal-body">
                <div className="input-group-heading text-[14px] flex justify-between">
                    {intl.formatMessage({ id: "i03025" })}
                    <Toggle defaultStatus={intent?.active}
                            onToggle={setActive}
                    />
                </div>

                <TextInputGroup disabled={!active}
                                invalid={!validation.name && !name?.trim()}
                                defaultValue={intent?.name}
                                label={intl.formatMessage({ id: "i03024" })}
                                onChange={setName}
                />

                <div className="flex flex-col gap-3">
                    <div className="input-group-heading flex justify-between">
                        {intl.formatMessage({ id: "i03026" }, { threshold: intentThreshold?.intent_threshold })}
                        <Toggle disabled={!active}
                                defaultStatus={intent?.config?.use_intent_threshold ?? false}
                                onToggle={v => setIntentThreshold(prev => ({ ...(prev ?? {}), use_intent_threshold: v }))}
                        />
                    </div>

                    <RangeSlider width="100%"
                                 disabled={!intentThreshold?.use_intent_threshold || !active}
                                 defaultValue={intent?.config?.intent_threshold || 50}
                                 onChange={v => setIntentThreshold(prev => ({ ...(prev ?? {}), intent_threshold: v }))}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-0.5">
                        <div className="input-group-heading flex justify-between">
                            {intl.formatMessage({ id: "i03027" })}
                        </div>
                        <div className="text-black-400 text-[12px] font-[400] leading-[1.5]">
                            {intl.formatMessage({ id: "i03028" })}
                        </div>
                    </div>
                    <AnswerEditor disalbed={!active}
                                  defaultAnswers={intent?.answers}
                                  updateAnswers={setAnswers}
                    />
                </div>

                <div className="modal-btn-group">
                    <Button size="sm"
                            variant="secondary"
                            disabled={isUpdating}
                            onClick={() => modal.close(INTENT_SETTING_MODAL_ID)}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isUpdating}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default IntentSettingModal;
